import React, { useCallback, useEffect, useState } from 'react';
import { Download, Inbox, Loader2, FileText, FileImage, File as FileIcon, Clock } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { api } from '../../lib/api';
import { useOrg } from '../../hooks/useOrg';

function fmtBytes(n) {
  if (!n) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function MimeIcon({ mime }) {
  if (mime?.startsWith('image/')) return <FileImage size={16} />;
  if (mime === 'application/pdf') return <FileText size={16} />;
  return <FileIcon size={16} />;
}

export default function SharedWithMeTab() {
  const { currentOrg } = useOrg();
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetch = useCallback(async () => {
    if (!currentOrg) return;
    setLoading(true); setError(null);
    try { setShares(await api.listSharedWithMe(currentOrg.id)); }
    catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, [currentOrg]);

  useEffect(() => { fetch(); }, [fetch]);

  const handleDownload = async (share) => {
    try {
      const { url } = await api.getDocumentDownloadUrl(currentOrg.id, share.document.id);
      window.open(url, '_blank');
    } catch (err) { setError(err.message); }
  };

  if (loading) {
    return (
      <div className="text-center py-12 text-muted-c text-[12px] flex items-center justify-center gap-2">
        <Loader2 size={14} className="animate-spin" /> Loading shared documents…
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-3 rounded-[10px] bg-danger/10 border border-danger/30 text-[12px] text-danger">
        {error}
      </div>
    );
  }

  if (shares.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="w-[48px] h-[48px] rounded-[14px] bg-tertiary mx-auto mb-3 flex items-center justify-center text-muted-c">
          <Inbox size={20} />
        </div>
        <p className="text-[13px] text-primary-c font-medium mb-1">No shared documents yet</p>
        <p className="text-[12px] text-muted-c">When a partner organization shares a document with you, it'll show up here.</p>
      </div>
    );
  }

  return (
    <div className="bg-elevated border border-theme rounded-[14px] overflow-hidden">
      {shares.map((share, i) => {
        const sharerName = share.shared_by_user
          ? `${share.shared_by_user.first_name || ''} ${share.shared_by_user.last_name || ''}`.trim()
          : '';
        const expiresIn = share.expires_at
          ? formatDistanceToNow(new Date(share.expires_at), { addSuffix: true })
          : null;

        return (
          <div key={share.id} className={`p-4 ${i > 0 ? 'border-t border-theme' : ''}`}>
            <div className="flex items-start gap-3">
              <div className="w-[36px] h-[36px] rounded-[10px] bg-tertiary flex items-center justify-center text-muted-c flex-shrink-0">
                <MimeIcon mime={share.document?.mime_type} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-medium text-primary-c truncate">
                  {share.document?.name}
                </div>
                <div className="text-[11px] text-muted-c flex items-center gap-2 flex-wrap mt-0.5">
                  <span>From <strong className="text-primary-c">{share.sender_org?.name}</strong></span>
                  {sharerName && <><span>·</span><span>shared by {sharerName}</span></>}
                  <span>·</span>
                  <span>{format(new Date(share.created_at), 'MMM d, yyyy')}</span>
                  <span>·</span>
                  <span>{fmtBytes(share.document?.size_bytes)}</span>
                </div>
                {share.message && (
                  <div className="mt-2 p-3 rounded-[10px] bg-tertiary border border-theme text-[12px] text-primary-c italic leading-relaxed">
                    "{share.message}"
                  </div>
                )}
                {expiresIn && (
                  <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-warning">
                    <Clock size={11} /> Access expires {expiresIn}
                  </div>
                )}
              </div>
              <button
                onClick={() => handleDownload(share)}
                className="px-3 py-2 rounded-[10px] text-[12px] font-semibold text-accent-light bg-accent-glow border border-strong hover:bg-hover-surface flex items-center gap-1.5 flex-shrink-0"
              >
                <Download size={13} /> Download
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
